import React, { useContext } from 'react';
import { AppContext } from '~/AppContext';
import { AppContextQuery } from '~/graphqlTypes/AppContextQuery';

import { RestrictedAccessProps } from './types';
import { _RestrictedAccessWrapperDiv, _RestrictedAccessWrapperFragment, explainNotAllowed } from './functions';

import './RestrictedAccess.scss';
import { consolidatePermissions } from '~/util/authentication';

export function parsePermissions( rawPermissions: string | string[] | undefined ): string[] {
  if ( Array.isArray( rawPermissions ) ) {
    return rawPermissions;
  }

  if ( !rawPermissions ) {
    return [];
  }

  return [rawPermissions];
}

/**
 * Check if the current user has some permission. This is an advisory client-side check that should
 * be mirrored on the server side.
 *
 * @param permissions The permissions to check for
 * @param context The current context
 * @param appId The ID of the app for which to check permissions
 * @param [supportSuperadmin=true] If true, users with the special role "GLOBAL_SUPERADMIN" will always have permission
 */
export function hasPermission(
  permissions: Array<string>,
  context: AppContextQuery,
  appId?: number,
  supportSuperadmin = true,
) {
  const effectivePermissions = consolidatePermissions( context, appId );
  return (
    permissions.every( ( required ) => effectivePermissions.has( required ) ) ||
    ( supportSuperadmin && context.me.role === 'GLOBAL_SUPERADMIN' )
  );
}

export const RestrictedAccess = ( props: RestrictedAccessProps ) => {
  const { children, permissions, notAllowed, wrapContents, appId, viewName, ...wrapperProps } = props;
  const parsedPermissions = parsePermissions( props.permissions );
  const context = useContext( AppContext );
  const allowed = hasPermission( parsedPermissions, context, appId );
  let Wrapper;

  if ( wrapContents === 'if-restricted' && !allowed ) {
    Wrapper = _RestrictedAccessWrapperDiv;
  } else if ( wrapContents === true ) {
    Wrapper = _RestrictedAccessWrapperDiv;
  } else {
    Wrapper = _RestrictedAccessWrapperFragment;
    delete wrapperProps.className;
  }

  let notAllowedFn: () => React.ReactNode;

  if ( notAllowed === 'explain' ) {
    notAllowedFn = () =>
      explainNotAllowed( {
        requiredPermissions: parsedPermissions,
        context,
        viewName,
        appId,
      } );
  } else {
    notAllowedFn = notAllowed || ( () => <></> );
  }

  return <Wrapper { ...wrapperProps }>{ allowed ? props.children : notAllowedFn() }</Wrapper>;
};

RestrictedAccess.displayName = 'RestrictedAccess';
